import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Camera, Check, Compass, GraduationCap, Hash, X,
} from 'lucide-react';
import { apiErrorMessage, authService, tagService, trailService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import { Brand, Button, Field, Loader } from '../components/ui';
import { AvailabilityForm, PlanPreview, toEnrollBody, usePlanPreview } from '../components/trails/TrailBits';
import { TRAIL_TYPES, fmtHoursRound } from '../utils/trails';
import './Onboarding.css';

const GOALS = [
  { key: 'certification', ...TRAIL_TYPES.certification, text: 'Passar num exame oficial' },
  { key: 'career', ...TRAIL_TYPES.career, text: 'Chegar a um cargo' },
  { key: 'project', ...TRAIL_TYPES.project, text: 'Entregar algo publicado' },
  { key: 'explore', label: 'Só explorar', icon: Compass, text: 'Sem meta por enquanto' },
];

const DEFAULT_AVAIL = { hours: 6, weekdays: [1, 2, 3, 4, 5, 6], examDate: '', noDate: true };

/**
 * Primeiro acesso (/boas-vindas): boas-vindas, objetivo, disponibilidade, temas e, no fim, o convite para o tour guiado.
 * Em /preferencias abre direto nos passos 2 a 4, com os valores atuais e "Salvar".
 */
function Onboarding() {
  const { user, setUser, isAdmin } = useAuth();
  const { courses, loadCourses } = useCourse();
  const navigate = useNavigate();
  const location = useLocation();
  const prefs = location.pathname === '/preferencias';
  const [step, setStep] = useState(() => (prefs ? 2 : Math.min(Math.max(user?.onboardingStep || 1, 1), 4)));
  const [name, setName] = useState(user?.name || '');
  const [displayName, setDisplayName] = useState(user?.displayName || (user?.name || '').split(' ')[0]);
  const [goal, setGoal] = useState(user?.goal || null);
  const [suggestions, setSuggestions] = useState(null);
  const [trailSlug, setTrailSlug] = useState(null);
  const [current, setCurrent] = useState(undefined); // trilha ativa (prefs)
  const [avail, setAvail] = useState(DEFAULT_AVAIL);
  const [tags, setTags] = useState(null);
  const [chosen, setChosen] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const tagsInit = useRef(false);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  // Trilha ativa e disponibilidade atual: base das preferências
  useEffect(() => {
    trailService.today().then(async (t) => {
      if (!t) { setCurrent(null); return; }
      const d = await trailService.detail(t.slug);
      setCurrent(d);
      setTrailSlug(d.slug);
      if (d.enrollment) {
        setAvail({
          hours: d.enrollment.hoursPerDay,
          weekdays: d.enrollment.weekdays,
          examDate: d.enrollment.examDate || '',
          noDate: !d.enrollment.examDate,
        });
      }
    }).catch(() => setCurrent(null));
  }, []);

  // Trilhas sugeridas para o objetivo
  useEffect(() => {
    if (!goal) return;
    setSuggestions(null);
    trailService.suggestions(goal === 'explore' ? undefined : goal).then(list => {
      setSuggestions(list);
      setTrailSlug(s => (s && list.some(x => x.slug === s && x.hasStages) ? s : list.find(x => x.hasStages)?.slug || null));
    }).catch(() => setSuggestions([]));
  }, [goal]);

  // Temas: da trilha (já marcados), dos cursos e os outros
  useEffect(() => {
    if (step !== 4) return;
    const slug = goal === 'explore' ? null : trailSlug;
    trailService.onboardingTags(slug).then(t => {
      setTags(t);
      if (!tagsInit.current) {
        tagsInit.current = true;
        const pre = new Set([...t.trail, ...t.courses, ...t.others].filter(x => x.followed).map(x => x.slug));
        // Primeiro acesso: os temas da trilha já vêm marcados
        if (!prefs) t.trail.forEach(x => pre.add(x.slug));
        setChosen(pre);
      }
    }).catch(() => setTags({ trail: [], courses: [], others: [] }));
  }, [step, goal, trailSlug, prefs]);

  const preview = usePlanPreview(step === 3 ? trailSlug : null, avail);

  if (!user) return <Navigate to="/login" replace />;
  if (!prefs && (user.onboarded || isAdmin)) return <Navigate to="/" replace />;

  const firstName = (displayName || name).trim().split(' ')[0] || 'tudo bem';
  const skipsAvailability = goal === 'explore' || !trailSlug;

  const persistStep = (n) => {
    if (!prefs) authService.updateOnboarding({ step: n }).then(setUser).catch(() => {});
  };

  const go = (n) => {
    setError(null);
    setStep(n);
    persistStep(n);
    window.scrollTo(0, 0);
  };

  const finish = async (withTour) => {
    try {
      setUser(await authService.updateOnboarding({ done: true, step: 5 }));
    } catch {
      // segue para a home mesmo assim
    }
    navigate('/', { replace: true, state: withTour ? { tour: true } : undefined });
  };

  const skipAll = () => finish(false);

  const next = async () => {
    setBusy(true);
    setError(null);
    try {
      if (step === 1) {
        setUser(await authService.updateProfile({ name, displayName }));
        go(2);
      } else if (step === 2) {
        if (!goal) { setError('Escolha um objetivo ou pule este passo.'); return; }
        setUser(await authService.updateOnboarding({ goal, step: skipsAvailability ? 4 : 3 }));
        go(skipsAvailability ? 4 : 3);
      } else if (step === 3) {
        const body = toEnrollBody(avail);
        if (current?.slug === trailSlug && current?.enrollment) await trailService.updateEnrollment(trailSlug, body, true);
        else await trailService.enroll(trailSlug, body);
        go(4);
      } else if (step === 4) {
        await applyTags();
        if (prefs) navigate(-1);
        else finish(true);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível salvar. Tente de novo.'));
    } finally {
      setBusy(false);
    }
  };

  const applyTags = async () => {
    if (!tags) return;
    const all = [...tags.trail, ...tags.courses, ...tags.others];
    const changes = all.filter(t => t.followed !== chosen.has(t.slug));
    for (const t of changes) {
      // eslint-disable-next-line no-await-in-loop
      await tagService.toggleFollow(t.slug);
    }
  };

  const back = () => {
    if (step === 4 && skipsAvailability) go(2);
    else go(step - 1);
  };

  const skipStep = () => {
    if (step === 2) go(4);
    else if (step === 3) go(4);
    else if (step === 4) { if (prefs) navigate(-1); else finish(true); }
  };

  // Preferências: só os passos 2 a 4, contados de 1 a 3
  const totalSteps = prefs ? 3 : 5;
  const steps = prefs ? [2, 3, 4] : [1, 2, 3, 4, 5];
  const shown = prefs ? step - 1 : step;

  return (
    <div className="ob">
      <header className="ob-top">
        <Brand size="sm" />
        <div className="ob-progress" aria-label={`Passo ${shown} de ${totalSteps}`}>
          <span>Passo <strong>{shown}</strong> de {totalSteps}</span>
          <span className="ob-progress__segs" aria-hidden="true">
            {steps.map(n => <i key={n} className={n < step ? 'is-done' : n === step ? 'is-current' : ''} />)}
          </span>
        </div>
        {prefs
          ? <button type="button" className="ob-skipall" onClick={() => navigate(-1)}><X size={16} aria-hidden="true" />Fechar</button>
          : <button type="button" className="ob-skipall" onClick={skipAll}>Pular tudo</button>}
      </header>

      <main className="ob-main">
        {step === 1 && (
          <section className="ob-step">
            <span className="ob-eyebrow">Senha trocada · conta pronta</span>
            <h1 className="ob-title">Olá, {firstName}.</h1>
            <p className="ob-lead">Quatro perguntas rápidas e o StudySpace monta seu plano. Leva uns 2 minutos, e tudo pode ser mudado depois no perfil.</p>
            <div className="ob-card ob-welcome">
              <AvatarPicker user={user} name={name} setUser={setUser} setError={setError} />
              <div className="ob-welcome__fields">
                <Field label="Nome" hint="Cadastrado pelo admin. Ajuste se quiser.">
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" />
                </Field>
                <Field label="Como quer ser chamado(a)" hint="Aparece na home e nos comentários.">
                  <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={60} />
                </Field>
                {courses.length > 0 && (
                  <p className="ob-courses">
                    <GraduationCap size={16} aria-hidden="true" />
                    <span>Cursos liberados para você: {joinNames(courses.map(c => c.name))}</span>
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="ob-step">
            <span className="ob-eyebrow">Objetivo</span>
            <h1 className="ob-title">Aonde você quer chegar?</h1>
            <p className="ob-lead">A gente sugere a trilha que leva até lá.</p>
            <div className="ob-goals" role="radiogroup" aria-label="Objetivo">
              {GOALS.map(g => (
                <button key={g.key} type="button" role="radio" aria-checked={goal === g.key} className={`ob-goal ${goal === g.key ? 'is-active' : ''}`} onClick={() => setGoal(g.key)}>
                  <span className="ob-goal__icon"><g.icon size={20} aria-hidden="true" /></span>
                  <strong>{g.label}</strong>
                  <span>{g.text}</span>
                  {goal === g.key && <span className="ob-goal__check"><Check size={13} strokeWidth={3} aria-hidden="true" /></span>}
                </button>
              ))}
            </div>
            {goal && goal !== 'explore' && (
              <div className="ob-suggest">
                <h2 className="ob-subtitle">Trilha sugerida</h2>
                {!suggestions && <Loader label="Buscando trilhas..." />}
                {suggestions?.length === 0 && (
                  <p className="ob-empty">Ainda não há trilhas de {GOALS.find(g => g.key === goal).label.toLowerCase()}. Você pode ver todas em Trilhas depois.</p>
                )}
                <div className="ob-trails" role="radiogroup" aria-label="Trilha sugerida">
                  {suggestions?.map((t, i) => (
                    <button
                      key={t.slug}
                      type="button"
                      role="radio"
                      aria-checked={trailSlug === t.slug}
                      disabled={!t.hasStages}
                      className={`ob-trail ${trailSlug === t.slug ? 'is-active' : ''}`}
                      onClick={() => setTrailSlug(t.slug)}
                    >
                      <span className="ob-radio" aria-hidden="true" />
                      <span className="ob-trail__text">
                        <span className="ob-trail__top">
                          {t.examCode && <code>{t.examCode}</code>}
                          {t.matchesCourses && i === 0 && <span className="ob-match">Combina com seus cursos</span>}
                          {!t.hasStages && <span className="ob-nostages">sem plano ainda</span>}
                        </span>
                        <strong>{t.title}</strong>
                        <span>
                          {[t.questions && `${t.questions} questões`, t.passPct && `${t.passPct}%`,
                            t.hasStages ? `${fmtHoursRound(t.totalMinutes)} de estudo` : t.areas && `${t.areas} áreas`].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {step === 3 && (
          <section className="ob-step">
            <span className="ob-eyebrow">Disponibilidade</span>
            <h1 className="ob-title">Quanto tempo você tem?</h1>
            <p className="ob-lead">Com isso o plano da trilha é distribuído no calendário.</p>
            <div className="ob-avail">
              <div className="ob-card"><AvailabilityForm value={avail} onChange={setAvail} /></div>
              <PlanPreview preview={preview} examDate={avail.noDate ? null : avail.examDate} />
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="ob-step">
            <span className="ob-eyebrow">Temas</span>
            <h1 className="ob-title">O que você quer ler?</h1>
            <p className="ob-lead">Artigos novos desses temas aparecem na sua home.</p>
            {!tags ? <Loader label="Carregando temas..." /> : (
              <>
                <div className="ob-card ob-tags">
                  <TagGroup title="Da sua trilha" aside={!prefs ? 'já marcados' : null} list={tags.trail} chosen={chosen} setChosen={setChosen} />
                  <TagGroup title="Dos seus cursos" list={tags.courses} chosen={chosen} setChosen={setChosen} />
                  <TagGroup title="Outros temas" list={tags.others} chosen={chosen} setChosen={setChosen} />
                  {!tags.trail.length && !tags.courses.length && !tags.others.length && (
                    <p className="ob-empty">Ainda não há temas com artigos. Quando a turma publicar, eles aparecem em Artigos.</p>
                  )}
                </div>
                <p className="ob-tagcount">
                  <Check size={15} aria-hidden="true" />
                  <strong>{chosen.size} {chosen.size === 1 ? 'tema' : 'temas'}</strong> {chosen.size === 1 ? 'seguido' : 'seguidos'} · dá para mudar em Artigos › Temas
                </p>
              </>
            )}
          </section>
        )}
        {error && <p className="ob-error" role="alert">{error}</p>}
      </main>

      <footer className="ob-foot">
        <div className="ob-foot__inner">
          {step > (prefs ? 2 : 1)
            ? <Button variant="ghost" icon={ArrowLeft} onClick={back}>Voltar</Button>
            : <span />}
          <span className="ob-foot__right">
            {step > 1 && <Button variant="ghost" onClick={skipStep}>Pular este passo</Button>}
            <Button onClick={next} disabled={busy || (step === 3 && !trailSlug) || (step === 1 && !name.trim())} iconRight={ArrowRight}>
              {step === 1 ? 'Começar' : prefs ? (step === 4 ? 'Salvar' : 'Salvar e continuar') : 'Continuar'}
            </Button>
          </span>
        </div>
      </footer>
    </div>
  );
}

function joinNames(names) {
  if (names.length <= 1) return <strong>{names[0]}</strong>;
  return (
    <>
      {names.slice(0, -1).map((n, i) => <React.Fragment key={n}>{i > 0 && ', '}<strong>{n}</strong></React.Fragment>)}
      {' e '}<strong>{names[names.length - 1]}</strong>
    </>
  );
}

function AvatarPicker({ user, name, setUser, setError }) {
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const initials = useMemo(() => (name || user.name).split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase(), [name, user.name]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { setError('Envie uma foto JPG ou PNG.'); return; }
    if (file.size > 2 * 1048576) { setError('A foto pode ter até 2 MB.'); return; }
    setBusy(true);
    setError(null);
    try {
      setUser(await authService.uploadAvatar(file));
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível enviar a foto.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-avatar">
      <span className="ob-avatar__pic">
        {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <span>{initials}</span>}
        <button type="button" className="ob-avatar__cam" onClick={() => input.current?.click()} aria-label="Enviar foto" disabled={busy}>
          <Camera size={17} aria-hidden="true" />
        </button>
      </span>
      <button type="button" className="ob-avatar__link" onClick={() => input.current?.click()} disabled={busy}>
        {busy ? 'Enviando…' : user.avatarUrl ? 'Trocar foto' : 'Enviar foto'}
      </button>
      <span className="ob-avatar__rule">JPG ou PNG · até 2 MB</span>
      <input ref={input} type="file" accept="image/jpeg,image/png" hidden onChange={pick} />
    </div>
  );
}

function TagGroup({ title, aside, list, chosen, setChosen }) {
  if (!list.length) return null;
  const toggle = (slug) => setChosen(s => {
    const next = new Set(s);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    return next;
  });
  return (
    <div className="ob-taggroup">
      <h2 className="ob-subtitle">{title}{aside && <span>{aside}</span>}</h2>
      <div className="ob-chips">
        {list.map(t => {
          const on = chosen.has(t.slug);
          return (
            <button key={t.slug} type="button" aria-pressed={on} className={`ob-chip ${on ? 'is-on' : ''}`} onClick={() => toggle(t.slug)}>
              {on ? <Check size={14} strokeWidth={2.8} aria-hidden="true" /> : <Hash size={14} strokeWidth={2.6} aria-hidden="true" />}
              {t.name}
              <span>{t.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Onboarding;
